import { createTheme, responsiveFontSizes } from "@mui/material";
import { grey } from "@mui/material/colors";
import { csCZ } from "@mui/material/locale";
import { alpha, getContrastRatio, type PaletteColor, type Theme } from "@mui/material/styles";

// const NORMAL_SIZE = "1rem";
export const FONT = "Verdana, Tahoma, sans-serif";

// const COMMON_FONT_STYLES = {
// 	fontFamily: FONT,
// 	letterSpacing: "0.00938em",
// };

export const primaryColors = {
	blue: "#002b59", // CMYK 100-65-0-60, sRGB 0-43-89, Lab 17 2 -33
	orange: "#ff5200", // CMYK 0-70-100-0, sRGB 255-82-0, Lab 61 66 85
	azure: "#00a1e0", // CMYK 90-5-0-0, sRGB 0-161-224, Lab 61 -24 -44
};

const lightPrimaryColors = {
	lightBlue: "#A4AEC9",
	lightOrange: "#FCD5B7",
	lightAzure: "#C3E4F9",
};

const veryLightPrimaryColors = {
	veryLightBlue: "#CFD3E3",
	veryLightOrange: "#FEEBDB",
	veryLightAzure: "#E3F2FD",
};

const neutralColors = {
	gray: "#737373", // CMYK 0-0-0-65, sRGB 115-115-115, Lab 48 0 0
	black: "#000000", // CMYK 0-0-0-100, sRGB 0-0-0, Lab 0 0 0
};

const lightNeutralColors = {
	lightGray: "#DCDCDC",
	lightBlack: "#000000",
};

const veryLightNeutralColors = {
	veryLightGray: "#ECECEC",
	veryLightBlack: "#000000",
};

/**
 * Extended base colors including complementary and additional tones.
 */
const additionalColors = {
	green: "#82bc00", // CMYK 53-0-100-0, sRGB 130-188-0, Lab 70 -37 73
	red: "#e40039", // CMYK 0-100-69-0, sRGB 228-0-57, Lab 49 75 39
	brown: "#914f4a", // CMYK 29-69-57-32, sRGB 145-79-74, Lab 42 28 16
	magenta: "#e70095", // CMYK 12-96-0-0, sRGB 231-0-149, Lab 51 80 -14
	turquoise: "#34a49a", // CMYK 72-7-42-7, sRGB 52-164-154, Lab 61 -34 -4
	yellow: "#faa800", // CMYK 0-40-96-0, sRGB 250-168-0, Lab 76 23 78
	blueviolet: "#6472b6", // CMYK 68-55-0-0, sRGB 100-114-182, Lab 49 9 -38
};

// const lightAdditionalColors = {
// 	lightGreen: "#E0ECC5",
// 	lightRed: "#F9C6BE",
// 	lightBrown: "#DAC4C0",
// 	lightMagenta: "#C1C8E0",
// 	lightTurquoise: "#CEE3E0",
// 	lightYellow: "#FEE5C0",
// 	lightBlueviolet: "#D2D2EB",
// };

const veryLightAdditionalColors = {
	veryLightGreen: "#F0F5E3",
	veryLightRed: "#FCE3E0",
	veryLightBrown: "#ECE1DD",
	veryLightMagenta: "#F8E5F1",
	veryLightTurquoise: "#E7F1F0",
	veryLightYellow: "#FFF2E1",
	veryLightBlueviolet: "#E8E9F6",
};

const szColors = {
	...primaryColors,
	...neutralColors,
	...additionalColors,
	...lightPrimaryColors,
	...lightNeutralColors,
	...lightPrimaryColors,
	...veryLightPrimaryColors,
	...veryLightNeutralColors,
	...veryLightAdditionalColors,
};

const getMainColor = (color: string) => color;
const getLightColor = (color: string) => alpha(color, 0.5);
const getDarkColor = (color: string) => alpha(color, 0.9);
const getContrastColor = (color: string) => (getContrastRatio(color, "#fff") > 4.5 ? "#fff" : "#111");

type SzMuiColors = Record<keyof typeof szColors, PaletteColor>;

const szMuiColors: SzMuiColors = Object.fromEntries(
	Object.entries(szColors).map(([key, color]) => [
		[key as keyof typeof szColors],
		{
			main: getMainColor(color),
			dark: getDarkColor(color),
			light: getLightColor(color),
			contrastText: getContrastColor(color),
		},
	]),
);

declare module "@mui/material/styles" {
	interface Palette {
		primaryColors: {
			orange: Palette["primary"]; // CMYK 0-70-100-0, sRGB 255-82-0, Lab 61 66 85
			blue: Palette["primary"]; // CMYK 100-65-0-60, sRGB 0-43-89, Lab 17 2 -33
			azure: Palette["primary"]; // CMYK 90-5-0-0, sRGB 0-161-224, Lab 61 -24 -44
		};
		neutralColors: {
			gray: Palette["primary"]; // CMYK 0-0-0-65, sRGB 115-115-115, Lab 48 0 0
			black: Palette["primary"]; // CMYK 0-0-0-100, sRGB 0-0-0, Lab 0 0 0
		};
		additionalColors: {
			green: Palette["primary"]; // CMYK 53-0-100-0, sRGB 130-188-0, Lab 70 -37 73
			red: Palette["primary"]; // CMYK 0-100-69-0, sRGB 228-0-57, Lab 49 75 39
			brown: Palette["primary"]; // CMYK 29-69-57-32, sRGB 145-79-74, Lab 42 28 16
			magenta: Palette["primary"]; // CMYK 12-96-0-0, sRGB 231-0-149, Lab 51 80 -14
			turquoise: Palette["primary"]; // CMYK 72-7-42-7, sRGB 52-164-154, Lab 61 -34 -4
			yellow: Palette["primary"]; // CMYK 0-40-96-0, sRGB 250-168-0, Lab 76 23 78
			blueviolet: Palette["primary"]; // CMYK 68-55-0-0, sRGB 100-114-182, Lab 49 9 -38
		};
	}

	interface PaletteOptions {
		primaryColors?: {
			orange?: PaletteOptions["primary"]; // CMYK 0-70-100-0, sRGB 255-82-0, Lab 61 66 85
			blue?: PaletteOptions["primary"]; // CMYK 100-65-0-60, sRGB 0-43-89, Lab 17 2 -33
			azure?: PaletteOptions["primary"]; // CMYK 90-5-0-0, sRGB 0-161-224, Lab 61 -24 -44
		};
		neutralColors?: {
			gray?: PaletteOptions["primary"]; // CMYK 0-0-0-65, sRGB 115-115-115, Lab 48 0 0
			black?: PaletteOptions["primary"]; // CMYK 0-0-0-100, sRGB 0-0-0, Lab 0 0 0
		};
		additionalColors?: {
			green?: PaletteOptions["primary"]; // CMYK 53-0-100-0, sRGB 130-188-0, Lab 70 -37 73
			red?: PaletteOptions["primary"]; // CMYK 0-100-69-0, sRGB 228-0-57, Lab 49 75 39
			brown?: PaletteOptions["primary"]; // CMYK 29-69-57-32, sRGB 145-79-74, Lab 42 28 16
			magenta?: PaletteOptions["primary"]; // CMYK 12-96-0-0, sRGB 231-0-149, Lab 51 80 -14
			turquoise?: PaletteOptions["primary"]; // CMYK 72-7-42-7, sRGB 52-164-154, Lab 61 -34 -4
			yellow?: PaletteOptions["primary"]; // CMYK 0-40-96-0, sRGB 250-168-0, Lab 76 23 78
			blueviolet?: PaletteOptions["primary"]; // CMYK 68-55-0-0, sRGB 100-114-182, Lab 49 9 -38
		};
	}

	interface BreakpointOverrides {
		mobile: true;
		tablet: true;
		laptop: true;
		desktop: true;
		sd: true;
		"480p": true;
		hd: true;
		"720p": true;
		fhd: true;
		fullhd: true;
		"1080p": true;
		qhd: true;
		"2k": true;
		uhd: true;
		"4k": true;
		ultraWide: true;
	}
}

const immutableColors = {
	primaryColors: {
		orange: szMuiColors.orange,
		blue: szMuiColors.blue,
		azure: szMuiColors.azure,
	},
	neutralColors: {
		gray: szMuiColors.gray,
		black: szMuiColors.black,
	},
	additionalColors: {
		green: szMuiColors.green,
		red: szMuiColors.red,
		brown: szMuiColors.brown,
		magenta: szMuiColors.magenta,
		turquoise: szMuiColors.turquoise,
		yellow: szMuiColors.yellow,
		blueviolet: szMuiColors.blueviolet,
	},
};

export const szBaseTheme = createTheme(
	{
		cssVariables: {
			colorSchemeSelector: "class",
		},
		colorSchemes: {
			light: {
				palette: {
					...immutableColors,
					primary: immutableColors.primaryColors.orange,
					secondary: immutableColors.primaryColors.blue,
					warning: immutableColors.additionalColors.yellow,
					info: immutableColors.primaryColors.azure,
					error: immutableColors.additionalColors.red,
					success: immutableColors.additionalColors.green,
				},
			},
			dark: {
				palette: {
					...immutableColors,
					primary: immutableColors.additionalColors.yellow,
					secondary: immutableColors.primaryColors.azure,
					warning: immutableColors.primaryColors.orange,
					info: immutableColors.primaryColors.azure,
					error: immutableColors.additionalColors.red,
					success: immutableColors.additionalColors.green,
				},
			},
		},
		palette: {
			...immutableColors,
			primary: immutableColors.primaryColors.orange,
			secondary: immutableColors.primaryColors.blue,
			warning: immutableColors.additionalColors.yellow,
			info: immutableColors.primaryColors.azure,
			error: immutableColors.additionalColors.red,
			success: immutableColors.additionalColors.green,
		},
		breakpoints: {
			values: {
				xs: 0,
				sm: 600,
				md: 900,
				lg: 1200,
				xl: 1536,
				mobile: 0,
				tablet: 640,
				laptop: 1024,
				desktop: 1200,
				sd: 480,
				"480p": 480,
				hd: 720,
				"720p": 720,
				fhd: 1080,
				fullhd: 1080,
				"1080p": 1080,
				qhd: 1440,
				"2k": 1440,
				uhd: 2160,
				"4k": 2160,
				ultraWide: 2160,
			},
		},
		typography: {
			fontFamily: "Verdana, Tahoma, sans-serif",
			h1: {
				fontFamily: FONT,
				fontSize: "2rem",
				lineHeight: 1,
				fontWeight: primaryColors.orange,
			},
			h2: {
				fontFamily: FONT,
				fontSize: "1.5rem",
				color: grey[700],
			},
			h3: {
				fontFamily: FONT,
				fontSize: "1.2rem",
				paddingBottom: "1rem",
				color: primaryColors.blue,
			},
			h4: {
				fontFamily: FONT,
				fontSize: "1.094rem",
				color: primaryColors.blue,
			},
			h5: {
				fontFamily: FONT,
				fontSize: "1rem",
			},
			h6: {
				fontFamily: FONT,
				fontSize: "0.969rem",
			},
			// h3TilesHeading: {
			// 	fontFamily: FONT,
			// 	fontSize: "1.094rem",
			// 	fontWeight: 500,
			// 	color: primaryColors.blue,
			// },
			body1: {
				fontFamily: FONT,
				color: grey[800],
			},
			body2: { fontFamily: FONT },
			caption: { fontFamily: FONT },
			overline: { fontFamily: FONT },
			// subtitle: { fontFamily: FONT },
			subtitle2: { fontFamily: FONT },
			// p: { fontSize: NORMAL_SIZE },
			// a: {
			// 	fontSize: NORMAL_SIZE,
			// 	textDecoration: "none",
			// },
			// bold: {
			// 	...COMMON_FONT_STYLES,
			// 	fontSize: NORMAL_SIZE,
			// 	fontWeight: "600",
			// },
			// szSubtitle1: {
			// 	fontFamily: FONT,
			// 	color: primaryColors.blue,
			// 	fontSize: "2rem",
			// 	lineHeight: "1em",
			// },
			// szSubtitle2Center: {
			//   fontFamily: FONT,
			//   fontSize: "1.5rem",
			//   letterSpacing: "0.2rem",
			//   width: "100%",
			//   textAlign: "center",
			//   fontWeight: "500",
			//   fontVariant: "small-caps",
			// },
			// szBodySmall: {
			// 	...COMMON_FONT_STYLES,
			// 	fontSize: "0.85rem",
			// },
			// szBodySmallBold: {
			// 	...COMMON_FONT_STYLES,
			// 	fontSize: "0.85rem",
			// 	fontWeight: 600,
			// },
			// accordionTransparentHeader: {
			// 	fontFamily: FONT,
			// 	color: grey[200],
			// 	fontSize: "1.1rem",
			// },
			// accordionTransparentHeaderErr: {
			// 	fontFamily: FONT,
			// 	fontWeight: 600,
			// 	color: grey[200],
			// 	fontSize: "1.1rem",
			// },
			// textAzure: {
			// 	fontFamily: FONT,
			// 	color: grey[200],
			// 	fontSize: "0.9rem",
			// },

			// Special typography
			// appHeader: {
			// 	fontFamily: FONT,
			// 	fontSize: "1.4rem",
			// 	letterSpacing: "0.1rem",
			// 	whiteSpace: "nowrap",
			// } as React.CSSProperties,
			// dropdownMenuShortKey: {
			//   fontFamily: FONT,
			//   letterSpacing: "0.1rem",
			//   paddingLeft: "2em",
			//   fontSize: "0.8em",
			//   textAlign: "right",
			//   paddingTop: "0.4em",
			// },
			// szLinkTab: {
			// 	fontFamily: FONT,
			// 	fontVariant: "small-caps",
			// 	fontSize: "0.8rem",
			// 	letterSpacing: "0.1rem",
			// },
			// szInputLabel: {
			//   fontFamily: FONT,
			//   color: "#757575", // Material-ui theming gray 600 - is not accessible (reason)
			//   fontSize: "1rem",
			//   fontWeight: 600,
			//   paddingRight: "0.5rem",
			//   lineHeight: 1.5,
			//   whiteSpace: "nowrap",
			// },
			// label: {
			// 	fontFamily: FONT,
			// 	color: grey[600],
			// 	fontSize: "1rem",
			// 	fontWeight: 600,
			// },
		},
	},
	csCZ,
);

export const szTheme = responsiveFontSizes(szBaseTheme);
// TODO: This warrants some cleanup later

// Used for styling a basic texts. Autochanges theme to dark or light properly, takes way less space
export const commonTextStyle = (theme: Theme) => ({
	color: theme?.vars?.palette.text.primary,
});

// Used for styling headers
export const commonHeaderStyle = (theme: Theme) => ({
	color: theme?.vars?.palette.secondary.main,
	fontWeight: "bolder",
});

// Used for styling input fields.
export const commonFieldStyle = (theme: Theme) => ({
	'& .MuiInputLabel-root': {
		color: theme.vars?.palette.text.primary,
	}
});

// Used for styling dropdown selects for MUI
export const commonDropDownStyle = (theme: Theme) => ({
	color: "text.primary",
	'&.Mui-selected': {
		backgroundColor: "secondary.light",
		color: "text.primary",
	},
});

// export const szTheme = createTheme(responsiveFontsizeSzTheme, {
// 	...responsiveFontsizeSzTheme,
// 	colorSchemes: { light: true, dark: true },
// 	cssVariables: true,
// });
